import { Loader2, Maximize2, PanelRightClose, Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useChatSidePanel } from '@/modules/chat/application/ChatSidePanelProvider';
import { useConversation } from '@/modules/chat/application/use-conversation';
import { SendMessageMutation } from '@/modules/common/types';
import { useProjectId } from '@/modules/project/hooks/use-projectId';

import { ChatConversationContainer } from './ChatConversationContainer';
import { ChatStartScreen, type StartedChatConversation } from './ChatStartScreen';

const RECENT_CONVERSATION_LIMIT = 5;
const NEW_CHAT_VALUE = '__new_chat__';

function getConversationTitle(
  conversation: ReturnType<typeof useConversation>['conversations'][number],
  fallback: string
) {
  const title = conversation.title || conversation.lastMessage?.content || fallback;

  return (
    title
      .replace(/[*_`#]/g, '')
      .replace(/\s+/g, ' ')
      .trim() || fallback
  );
}

type InitialConversationMessages = {
  conversationId: string;
  messages: SendMessageMutation['sendMessage']['messages'];
};

export function ChatSidePanel({ className }: { className?: string }) {
  const { isOpen } = useChatSidePanel();

  if (!isOpen) {
    return null;
  }

  return <ChatSidePanelContent className={className} />;
}

function ChatSidePanelContent({ className }: { className?: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    conversationId,
    requestContext,
    openChatPanel,
    closeChatPanel,
    startNewChat,
    consumeRequestContext,
  } = useChatSidePanel();
  const { conversations, conversationsLoading } = useConversation();
  const { projectId } = useProjectId();
  const [initialConversationMessages, setInitialConversationMessages] =
    useState<InitialConversationMessages | null>(null);

  const recentConversations = conversations.slice(0, RECENT_CONVERSATION_LIMIT);
  const selectorValue = recentConversations.some(
    (conversation) => conversation.id === conversationId
  )
    ? conversationId
    : NEW_CHAT_VALUE;
  const selectedInitialMessages =
    initialConversationMessages?.conversationId === conversationId
      ? initialConversationMessages.messages
      : null;

  const handleSelectConversation = (nextValue: string) => {
    setInitialConversationMessages(null);

    if (nextValue === NEW_CHAT_VALUE) {
      startNewChat();
      return;
    }

    openChatPanel({ conversationId: nextValue });
  };

  const handleStartedConversation = (conversation: StartedChatConversation) => {
    setInitialConversationMessages({
      conversationId: conversation.conversationId,
      messages: conversation.initialMessages,
    });
    openChatPanel({ conversationId: conversation.conversationId });
  };

  const handleFullView = () => {
    const state = requestContext ? { requestContext } : undefined;
    navigate(conversationId ? `/chat/${conversationId}` : '/chat', state ? { state } : undefined);
    closeChatPanel();
  };

  const handleNewChat = () => {
    setInitialConversationMessages(null);
    startNewChat();
  };

  return (
    <>
      <button
        type="button"
        aria-label={t('modules.mavue2.chat.closeSidePanel')}
        className="fixed inset-0 z-40 bg-black/20 md:hidden"
        onClick={closeChatPanel}
      />
      <aside
        className={cn(
          'fixed inset-y-0 right-0 z-50 flex w-[min(100vw,420px)] flex-col border-l bg-background shadow-2xl md:static md:z-auto md:h-full md:w-[420px] md:shrink-0 md:shadow-none',
          className
        )}
      >
        <div className="flex h-14 shrink-0 items-center gap-2 px-3">
          <div className="min-w-0 flex-1">
            <Select
              value={selectorValue ?? NEW_CHAT_VALUE}
              onValueChange={handleSelectConversation}
            >
              <SelectTrigger
                aria-label={t('modules.mavue2.chat.selectConversation')}
                className="h-9 w-fit max-w-full min-w-0 justify-start gap-1 border-0 bg-transparent px-2 shadow-none [&>svg]:shrink-0 [&>span]:min-w-0 [&>span]:truncate"
              >
                <SelectValue placeholder={t('modules.mavue2.navigation.newChat')} />
              </SelectTrigger>
              <SelectContent align="start" className="w-80">
                <SelectItem value={NEW_CHAT_VALUE}>
                  {t('modules.mavue2.navigation.newChat')}
                </SelectItem>
                {recentConversations.map((conversation) => (
                  <SelectItem key={conversation.id} value={conversation.id}>
                    {getConversationTitle(
                      conversation,
                      t('modules.mavue2.chat.untitledConversation')
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="ml-auto flex shrink-0 items-center gap-1">
            {conversationsLoading && (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            )}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={handleNewChat}
            >
              <Plus className="h-4 w-4" />
              <span className="sr-only">{t('modules.mavue2.navigation.newChat')}</span>
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={handleFullView}
            >
              <Maximize2 className="h-4 w-4" />
              <span className="sr-only">{t('modules.mavue2.chat.openFullView')}</span>
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={closeChatPanel}
            >
              <PanelRightClose className="h-4 w-4" />
              <span className="sr-only">{t('modules.mavue2.chat.closeSidePanel')}</span>
            </Button>
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-hidden">
          {conversationId ? (
            <ChatConversationContainer
              conversationId={conversationId}
              initialMessages={selectedInitialMessages}
              projectId={projectId}
              requestContext={requestContext}
              onRequestContextConsumed={consumeRequestContext}
              variant="panel"
            />
          ) : (
            <ChatStartScreen
              variant="panel"
              requestContext={requestContext}
              onRequestContextConsumed={consumeRequestContext}
              onConversationStarted={handleStartedConversation}
            />
          )}
        </div>
      </aside>
    </>
  );
}
